/**
 * 絵文字とテキスト処理のユーティリティ関数
 */

// 絵文字を検出する正規表現（Unicode絵文字ブロック）- 数字・記号の誤検出を回避
const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F170}-\u{1F251}]|[\u{1F004}]|[\u{1F17E}]|[\u{3030}]|[\u{2B50}]|[\u{2B55}]|[\u{2934}]|[\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}]|[\u{2B1C}]|[\u{3297}]|[\u{3299}]|[\u{00AE}]|[\u{00A9}]|[\u{203C}]|[\u{2049}]|[\u{2122}]|[\u{2139}]/gu;

/**
 * テキスト内の絵文字を検出して、.emojiクラス付きspanで囲む
 * @param {string} text - 処理するテキスト
 * @returns {string} - 処理されたHTML文字列
 */
export const processEmojiInText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // 絵文字を <span class="emoji">絵文字</span> で囲む
  return text.replace(EMOJI_REGEX, (emoji) => {
    return `<span class="emoji">${emoji}</span>`;
  });
};

/**
 * テキスト内の絵文字を検出してReact要素として返す
 * @param {string} text - 処理するテキスト
 * @returns {Array} - React要素の配列
 */
export const processEmojiForReact = (text) => {
  if (!text || typeof text !== 'string') {
    return [text];
  }

  const parts = [];
  let lastIndex = 0;
  let match;

  // 絵文字を検出して配列に分解
  const regex = new RegExp(EMOJI_REGEX);
  while ((match = regex.exec(text)) !== null) {
    // 絵文字前のテキスト
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
        key: `text-${lastIndex}-${match.index}`
      });
    }

    // 絵文字部分
    parts.push({
      type: 'emoji',
      content: match[0],
      key: `emoji-${match.index}`
    });

    lastIndex = match.index + match[0].length;
  }

  // 残りのテキスト
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
      key: `text-end-${lastIndex}`
    });
  }

  return parts;
};

/**
 * 絵文字かどうかを判定する
 * @param {string} char - 判定する文字
 * @returns {boolean} - 絵文字の場合true
 */
export const isEmoji = (char) => {
  return EMOJI_REGEX.test(char);
};

/**
 * テキストに絵文字が含まれているかチェック
 * @param {string} text - チェックするテキスト
 * @returns {boolean} - 絵文字が含まれている場合true
 */
export const containsEmoji = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }
  return EMOJI_REGEX.test(text);
};

/**
 * メンション、ハッシュタグ、絵文字を含むテキストを完全に処理
 * @param {string} text - 処理するテキスト
 * @returns {Array} - 処理されたパーツの配列
 */
export const formatCompleteText = (text) => {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '', key: 'empty-text' }];
  }

  const parts = [];
  let lastIndex = 0;

  // メンション、ハッシュタグ、絵文字を統合した正規表現（数字・記号の誤検出を回避）
  const combinedRegex = /(@[a-zA-Z0-9_]+)|(#[a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]+)|([\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F170}-\u{1F251}]|[\u{1F004}]|[\u{1F17E}]|[\u{3030}]|[\u{2B50}]|[\u{2B55}]|[\u{2934}]|[\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}]|[\u{2B1C}]|[\u{3297}]|[\u{3299}]|[\u{00AE}]|[\u{00A9}]|[\u{203C}]|[\u{2049}]|[\u{2122}]|[\u{2139}])/gu;

  let match;
  while ((match = combinedRegex.exec(text)) !== null) {
    // マッチ前のテキスト
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
        key: `text-${lastIndex}-${match.index}`
      });
    }

    // マッチしたパターンを判定
    if (match[1]) {
      // メンション
      const username = match[1].substring(1);
      parts.push({
        type: 'mention',
        content: match[1],
        username: username,
        key: `mention-${match.index}`
      });
    } else if (match[2]) {
      // ハッシュタグ
      const hashtag = match[2].substring(1);
      parts.push({
        type: 'hashtag',
        content: match[2],
        hashtag: encodeURIComponent(hashtag),
        rawHashtag: hashtag,
        key: `hashtag-${match.index}`
      });
    } else if (match[3]) {
      // 絵文字
      parts.push({
        type: 'emoji',
        content: match[3],
        key: `emoji-${match.index}`
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // 残りのテキスト
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
      key: `text-end-${lastIndex}`
    });
  }

  return parts;
}; 